import { doc, getDoc, setDoc, deleteDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../firebase';
import { PUZZLES_COLLECTION } from '../../services/crosswordService';

const getHelsinkiDate = () => new Date().toLocaleDateString('en-CA', { timeZone: 'Europe/Helsinki' });
const parseHelsinkiDate = (dateId) => new Date(dateId.split('-').reverse().join('-') + 'T00:00:00');
const shouldBePublished = (dateId) => {
  const [todayYear, todayMonth, todayDay] = getHelsinkiDate().split('-');
  return parseHelsinkiDate(dateId) <= parseHelsinkiDate(`${todayDay}-${todayMonth}-${todayYear}`);
};
const isToday = (dateId) => {
  const [todayYear, todayMonth, todayDay] = getHelsinkiDate().split('-');
  return dateId === `${todayDay}-${todayMonth}-${todayYear}`;
};

export const moveCrosswordData = async (fromDateId, toDateId) => {
  try {
    const fromDoc = await getDoc(doc(db, PUZZLES_COLLECTION, fromDateId));
    const toDoc = await getDoc(doc(db, PUZZLES_COLLECTION, toDateId));
    
    if (!fromDoc.exists()) throw new Error(`Source puzzle (${fromDateId}) not found`);
    
    const fromData = fromDoc.data();
    const toData = toDoc.exists() ? toDoc.data() : null;
    
    if (toData && (toData.words?.length > 0 || toData.blackSquares?.length > 0)) {
      if (!window.confirm(`${toDateId} already has a crossword. This will swap the crosswords between ${fromDateId} and ${toDateId}. Continue?`)) return false;
    }
    
    const extractCrosswordData = (data) => ({ gridSize: data.gridSize, words: data.words || [], blackSquares: data.blackSquares || [] });
    const fromCrossword = extractCrosswordData(fromData);
    const toCrossword = toData ? extractCrosswordData(toData) : { gridSize: { width: 6, height: 7 }, words: [], blackSquares: [] };
    
    const toContentVersion = window.crypto.randomUUID();
    const updatedToData = {
      id: toDateId,
      ...fromCrossword,
      ready: fromCrossword.words.length > 0 && fromCrossword.words.every(w => w.clue?.trim()),
      published: shouldBePublished(toDateId),
      isToday: isToday(toDateId),
      contentVersion: toContentVersion
    };

    await setDoc(doc(db, PUZZLES_COLLECTION, toDateId), updatedToData);

    // Get current manifest
    const manifestRef = doc(db, 'metadata', 'crosswordPuzzles');
    const manifestDoc = await getDoc(manifestRef);
    const currentManifest = manifestDoc.exists() ? (manifestDoc.data().manifest || {}) : {};

    if (toCrossword.words.length > 0) {
      // Swapping puzzles - update both versions in manifest
      const fromContentVersion = window.crypto.randomUUID();
      const updatedFromData = {
        ...fromData,
        ...toCrossword,
        ready: toCrossword.words.length > 0 && toCrossword.words.every(w => w.clue?.trim()),
        published: shouldBePublished(fromDateId),
        isToday: isToday(fromDateId),
        contentVersion: fromContentVersion
      };
      await setDoc(doc(db, PUZZLES_COLLECTION, fromDateId), updatedFromData);

      // Update manifest for both puzzles
      await setDoc(manifestRef, {
        manifest: {
          ...currentManifest,
          [toDateId]: toContentVersion,
          [fromDateId]: fromContentVersion
        },
        lastUpdated: serverTimestamp()
      }, { merge: true });
    } else {
      // Moving to empty date - delete from source, update manifest
      await deleteDoc(doc(db, PUZZLES_COLLECTION, fromDateId));

      // Remove old ID, add new ID to manifest
      delete currentManifest[fromDateId];
      await setDoc(manifestRef, {
        manifest: {
          ...currentManifest,
          [toDateId]: toContentVersion
        },
        lastUpdated: serverTimestamp()
      }, { merge: true });
    }

    return true;
  } catch (error) {
    console.error('Error moving crossword:', error);
    throw error;
  }
};